import {
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import { useState } from "react";
import { TagFormat, TagType } from "../../common/models/schema/TagFormat";

interface CreateTagFormProps {
  onCreateTag: (tag: TagFormat) => Promise<void>;
  createError: string | null;
  getTagTypeLabel: (type: TagType) => string;
}

const CreateTagForm = ({
  onCreateTag,
  createError,
  getTagTypeLabel,
}: CreateTagFormProps) => {
  const [showForm, setShowForm] = useState(false);
  const [newTag, setNewTag] = useState({
    name: "",
    format: "",
    description: "",
    examples: "",
    type: TagType.STRING,
  });
  const [createLoading, setCreateLoading] = useState(false);

  const handleCreateTag = async () => {
    if (!newTag.name.trim() || !newTag.format.trim()) {
      return;
    }

    try {
      setCreateLoading(true);
      await onCreateTag(newTag as TagFormat);
      setNewTag({
        name: "",
        format: "",
        description: "",
        examples: "",
        type: TagType.STRING,
      });
      setShowForm(false);
    } catch (error) {
      console.error("Error creating tag:", error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | TagType) => {
    setNewTag((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Tags Manager
        </Typography>
        <Button
          variant="contained"
          onClick={() => setShowForm(!showForm)}
          sx={{ ml: 2 }}
        >
          Create Tag
        </Button>
      </Box>

      {showForm && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Create New Tag
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label="Name"
                  value={newTag.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  label="Format"
                  value={newTag.format}
                  onChange={(e) => handleInputChange("format", e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newTag.type}
                    label="Type"
                    onChange={(e) =>
                      handleInputChange("type", e.target.value as TagType)
                    }
                  >
                    <MenuItem value={TagType.STRING}>
                      {getTagTypeLabel(TagType.STRING)}
                    </MenuItem>
                    <MenuItem value={TagType.LIST}>
                      {getTagTypeLabel(TagType.LIST)}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TextField
                label="Description"
                value={newTag.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                size="small"
                multiline
                rows={2}
              />
              <TextField
                label="Examples"
                value={newTag.examples}
                onChange={(e) => handleInputChange("examples", e.target.value)}
                size="small"
                multiline
                rows={2}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleCreateTag}
                  disabled={
                    !newTag.name.trim() ||
                    !newTag.format.trim() ||
                    createLoading
                  }
                >
                  {createLoading ? <CircularProgress size={20} /> : "Create"}
                </Button>
                <Button variant="outlined" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
            {createError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {createError}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default CreateTagForm;
